import React, { useState, useEffect } from "react";
import { Button, message } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import Service from "../../service";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { updateAttendance } from "../../appRedux/actions/Common";
import "./PunchButton.css";

const PunchButton = () => {
  const authUser = useSelector((state) => state.auth.authUser);
  const dispatch = useDispatch();
  const location = useLocation();
  const [nextAction, setNextAction] = useState("IN"); // What the next punch should be
  const [lastPunchTime, setLastPunchTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [clockInOutDevice, setClockInOutDevice] = useState(null);
  const [deviceLoaded, setDeviceLoaded] = useState(false);

  // Get current punch status on component mount
  useEffect(() => {
    if (authUser?._id) {
      getPunchStatus();
      fetchShiftDevice();
    } else {
      // If no authUser, still show button but disable it
      setCheckingStatus(false);
    }
  }, [authUser?._id, location.pathname]);

  // When punch is recorded elsewhere (e.g. ChatBot prompt), refresh header status
  useEffect(() => {
    const onPunchRecorded = () => {
      if (authUser?._id) {
        getPunchStatus();
      }
    };
    window.addEventListener("punchRecorded", onPunchRecorded);
    return () => window.removeEventListener("punchRecorded", onPunchRecorded);
  }, [authUser?._id]);

  const fetchShiftDevice = async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: `${Service.empById}/${authUser._id}`,
      });
      if (response.data && (response.data.status === 200 || response.data.status === 1)) {
        const device = response.data.data?.shiftDetails?.clock_in_out_device;
        setClockInOutDevice(device || null);
      }
    } catch (error) {
      // ignore - handled by deviceLoaded gate below
    } finally {
      setDeviceLoaded(true);
    }
  };

  const getPunchStatus = async () => {
    try {
      setCheckingStatus(true);
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.punchStatus,
        body: {
          employee_id: authUser._id,
        },
      });

      if (response.data && response.data.status === 200) {
        // Use nextAction to determine what button should show
        setNextAction(response.data.data.nextAction || "IN");
        setLastPunchTime(response.data.data.lastPunchTime);
      }
    } catch (error) {
      console.error("Error fetching punch status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handlePunch = async () => {
    if (!authUser?._id) {
      message.error("User not authenticated");
      return;
    }

    try {
      setLoading(true);
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.punchInOut,
        body: {
          employee_id: authUser._id,
          source: "web",
        },
      });

      if (response.data && response.data.status === 200) {
        const newDirection = response.data.data.direction;
        setLastPunchTime(response.data.data.time);
        message.success(response.data.message || `Punch ${newDirection} recorded successfully`);
        
        // Update nextAction based on what was just recorded
        // If we just punched IN, next should be OUT, and vice versa
        setNextAction(newDirection === "IN" ? "OUT" : "IN");

        // Emit custom event to trigger refresh in other components
        window.dispatchEvent(new CustomEvent('punchRecorded', {
          detail: {
            direction: newDirection,
            time: response.data.data.time,
            timestamp: response.data.data.timestamp
          }
        }));

        // The window event above only reaches this button (and ChatBot) -
        // the Dashboard's "In Time"/"Total Hours" cards read from a
        // completely separate mechanism (NewUserDashboard's getinTime() and
        // AttendanceDashboard's getmultiplepunch(), both gated on Redux's
        // attendanceUpdated counter, same as EmployeeInout.js already does
        // for its own punch action). Without this, punching from this
        // header button never refreshed the Dashboard at all.
        dispatch(updateAttendance());
      } else {
        message.error(response.data?.message || "Failed to record punch");
      }
    } catch (error) {
      console.error("Error recording punch:", error);
      message.error(error.response?.data?.message || "Failed to record punch. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (checkingStatus) {
      return "Loading...";
    }
    return nextAction === "OUT" ? "Punch Out" : "Punch In";
  };

  const getButtonType = () => {
    return nextAction === "OUT" ? "default" : "primary";
  };

  // Don't render if no authUser
  if (!authUser?._id) {
    return null;
  }

  // Wait for shift device fetch before deciding to render
  if (!deviceLoaded) {
    return null;
  }

  // Only show punch button if shift allows web punching ("web" or "both")
  if (clockInOutDevice !== "web" && clockInOutDevice !== "both") {
    return null;
  }

  return (
    <div style={{ display: 'inline-block', visibility: 'visible' }}>
      <Button
        type={getButtonType()}
        icon={<ClockCircleOutlined />}
        onClick={handlePunch}
        loading={loading || checkingStatus}
        disabled={checkingStatus}
        className="punch-button"
        title={lastPunchTime ? `Last punch: ${lastPunchTime}` : "Record your attendance"}
      >
        {getButtonText()}
      </Button>
    </div>
  );
};

export default PunchButton;

